export const Field = ({ label, children, hint, testId }) => (
  <label className="flex flex-col gap-2" data-testid={testId}>
    <span className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-400">{label}</span>
    {children}
    {hint && <span className="text-xs text-zinc-500">{hint}</span>}
  </label>
);

export const Input = ({ value = "", onChange, ...rest }) => (
  <input
    type="text"
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="bg-transparent border-b-2 border-white/15 focus:border-brand-light outline-none py-2.5 text-white placeholder:text-zinc-600 transition-colors"
    {...rest}
  />
);

export const Textarea = ({ value = "", onChange, rows = 4, ...rest }) => (
  <textarea
    value={value}
    rows={rows}
    onChange={(e) => onChange(e.target.value)}
    className="bg-black/30 border border-white/15 focus:border-brand-light outline-none p-3 text-white placeholder:text-zinc-600 transition-colors resize-y"
    {...rest}
  />
);

export const Select = ({ value, onChange, options = [], ...rest }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="bg-black/30 border border-white/15 focus:border-brand-light outline-none py-2.5 px-3 text-white transition-colors"
    {...rest}
  >
    {options.map((o) => (
      <option key={o.value} value={o.value} className="bg-[#0a0a0a]">
        {o.label}
      </option>
    ))}
  </select>
);

export const Toggle = ({ value, onChange, label }) => (
  <label className="flex items-center gap-3 cursor-pointer">
    <button
      type="button"
      onClick={() => onChange(!value)}
      className={`h-6 w-11 rounded-full border transition-colors relative ${
        value ? "bg-brand border-brand" : "bg-transparent border-white/20"
      }`}
    >
      <span
        className={`absolute top-0.5 h-5 w-5 bg-white rounded-full transition-transform ${
          value ? "translate-x-5" : "translate-x-0.5"
        }`}
      />
    </button>
    {label && <span className="text-sm text-zinc-300">{label}</span>}
  </label>
);

export const SaveBar = ({ saving, dirty, onSave, onReset }) => (
  <div className={`sticky bottom-4 mt-8 transition-all ${dirty ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
    <div className="bento-card flex items-center justify-between p-3 sm:p-4 gap-3 backdrop-blur bg-[#0c0c0c]/95 border-brand/40">
      <div className="text-xs tracking-[0.2em] uppercase font-bold text-brand-light flex items-center gap-2">
        <span className="h-2 w-2 bg-brand-light rounded-full animate-pulse" />
        Unsaved changes
      </div>
      <div className="flex items-center gap-2">
        {onReset && (
          <button
            type="button"
            onClick={onReset}
            className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400 hover:text-white px-3 py-2 transition-colors"
            data-testid="cms-reset"
          >
            Discard
          </button>
        )}
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="btn-primary !py-2.5 !px-5 !text-sm disabled:opacity-70 disabled:cursor-not-allowed"
          data-testid="cms-save"
        >
          {saving ? "Saving…" : "Save Changes"}
        </button>
      </div>
    </div>
  </div>
);

export const PageHeader = ({ overline, title, description, actions }) => (
  <header className="mb-10">
    {overline && <div className="overline">{overline}</div>}
    <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
      <h1 className="font-display text-4xl sm:text-5xl uppercase leading-[0.95] tracking-tight">
        {title}
      </h1>
      {actions}
    </div>
    {description && <p className="mt-3 text-sm text-zinc-400 max-w-2xl">{description}</p>}
  </header>
);

export const Card = ({ title, description, children, className = "" }) => (
  <section className={`bento-card p-5 sm:p-6 mb-6 ${className}`}>
    {title && <h2 className="font-display text-xl uppercase mb-1">{title}</h2>}
    {description && <p className="text-sm text-zinc-400 mb-5">{description}</p>}
    {children}
  </section>
);
